import React, { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import { Dropdown } from 'react-bootstrap';

import { EventStatus, MapTypes } from '../../enums';
import { useEventRoute, useFleet, useHasAccess, useLocalStorage, useRouteEventTypes, useToggle } from '../../hooks';
import { Event, ParsedRouteDefinition, Route } from '../../interfaces';
import {
  addUserKML,
  removeUserKML,
  setLocalOverride,
  toggleHiddenRouteId,
  toggleUserKMLVisibility,
  useHiddenRouteIds,
  useLocalOverrides,
  useUserKMLs,
} from '../../queries';
import { EventsService, RouteService } from '../../services';
import { downloadTextFile, getEventCoords, getOriginalEventCoords, hash, isAggregableEvent, nothing } from '../../utils';
import { Alert, Button, InternalOnly, Kebab, Relative, Tooltip } from '../atoms';
import { GoogleMap } from '../map/googleMap';
import { queryClient } from '../queryClient';
import { extractGoogleCoords, parseLocalKMLFile } from '../reviewGridMatchedEvents/KMLParser';
import { clearFromMap, drawKMLPolygonsAsPointsonMap, drawKMLonMap } from '../reviewGridMatchedEvents/KMLRenderer';
import { ButtonContainer, createRotatedIcon, HiddenFileInput } from './atoms';
import { SettingsModal } from './settingsModal';

interface Props {
  event: Event;
  states?: Record<number, EventStatus>;
  className?: string;
  style?: React.CSSProperties;
}

const icons = {
  green: 'http://maps.google.com/mapfiles/ms/icons/green-dot.png',
  red: 'http://maps.google.com/mapfiles/ms/icons/red-dot.png',
  orange: 'http://maps.google.com/mapfiles/ms/icons/orange-dot.png',
  gray: 'http://maps.google.com/mapfiles/ms/icons/ltblue-dot.png',
  bus: 'https://maps.gstatic.com/mapfiles/ms2/micons/bus.png',
  directed: '/directedLocation.png',
};

let StreetViewService: google.maps.StreetViewService;
let PanoramaSources: google.maps.StreetViewSource[] = [];
let GoogleStreetViewSource: google.maps.StreetViewSource;

export const StreetView: FC<Props> = (props) => {
  const { event, states, style } = props;
  const subEvents = event?.sub_events ?? [];
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [mapType, setMapType] = useState<MapTypes>(MapTypes.Hybrid);
  const markers = useRef<google.maps.Marker[]>([]);
  const polylines = useRef<google.maps.Polyline[]>([]);
  const enforcementMarkersForSV = useRef<google.maps.Marker[]>([]);
  const [heading, setHeading] = useState<number>(0);
  const isAggregated = isAggregableEvent(event);
  const fleet = useFleet();
  const [remoteKML, setRemoteKML] = useState<ParsedRouteDefinition | null>(null);
  const [eventRouteHidden, setEventRouteHidden] = useState(false);
  const { data: localOverridesData } = useLocalOverrides();
  const { data: hiddenRouteIdsData } = useHiddenRouteIds();
  const { data: userKMLs = [] } = useUserKMLs();
  const [pendingOverrideRouteId, setPendingOverrideRouteId] = useState<number | null>(null);
  const route = useEventRoute(event);
  const [settingsModal, toggleSettings] = useToggle(false);
  const [projectedLocation] = useLocalStorage('useProjectedLocation', false);
  const isInternalUser = useHasAccess();

  // Tracks the last confirmed Google-official panorama ID so the navigation
  // guard can revert to it if the user clicks into a community panorama.
  const lastGoodPanoRef = useRef<string | null>(null);

  // The pano ID we're programmatically navigating to during setupStreetView.
  // The navigation guard ignores pano_changed events until this exact ID lands,
  // then clears itself. A boolean flag isn't safe here because setPosition is
  // async — it fires pano_changed after the JS call stack unwinds, so a boolean
  // reset in a finally block is already gone by the time the event fires.
  const pendingSetupPanoRef = useRef<string | null>(null);

  const getStreetView = () => map?.getStreetView();
  const panorama = getStreetView();
  const panoramaVisible = panorama?.getVisible();
  const [panoramaVisibleState, setPanoramaVisibleState] = useState(panoramaVisible);

  const [allRouteEventTypes] = useRouteEventTypes();
  const routeEventTypes = allRouteEventTypes.filter((et) => !et.includes('_MATCH'));

  const kmlFilename = event?.route_name;

  const computeEffectiveKML = (): ParsedRouteDefinition | null => {
    const allPolygons: ParsedRouteDefinition['polygons'] = [];
    const allMarkers: ParsedRouteDefinition['markers'] = [];

    if (route && route.is_global) {
      const isHidden = hiddenRouteIdsData?.has(route.id) ?? false;
      if (!isHidden) {
        const effective = localOverridesData?.[route.id]?.data ?? remoteKML;
        if (effective) {
          allPolygons.push(...effective.polygons);
          allMarkers.push(...effective.markers);
        }
      }
    } else if (!eventRouteHidden) {
      const effective = (route?.id ? localOverridesData?.[route.id]?.data : null) ?? remoteKML;
      if (effective) {
        allPolygons.push(...effective.polygons);
        allMarkers.push(...effective.markers);
      }
    }

    userKMLs.forEach((kml) => {
      if (kml.visible) {
        allPolygons.push(...kml.data.polygons);
        allMarkers.push(...kml.data.markers);
      }
    });

    return allPolygons.length > 0 || allMarkers.length > 0 ? { polygons: allPolygons, markers: allMarkers } : null;
  };

  let averageLat = event?.lat;
  let averageLong = event?.lon;

  if (isAggregated) {
    averageLat = subEvents.reduce((acc, curr) => acc + curr.lat, 0) / subEvents.length;
    averageLong = subEvents.reduce((acc, curr) => acc + curr.lon, 0) / subEvents.length;
  }

  const center: google.maps.LatLngLiteral = {
    lat: averageLat,
    lng: averageLong,
  };

  useEffect(() => {
    const loadLibs = async () => {
      const library = (await google.maps.importLibrary('streetView')) as any;
      StreetViewService = library.StreetViewService;
      // Store both source constants once the library is loaded.
      PanoramaSources = [library.StreetViewSource.GOOGLE, library.StreetViewSource.OUTDOOR];
      GoogleStreetViewSource = library.StreetViewSource.GOOGLE;
    };
    loadLibs();
  }, []);

  const clearAllFromMap = () => {
    clearFromMap(polylines.current, map);
    clearFromMap(enforcementMarkersForSV.current, map);
  };

  const getIconImage = (event: Event): string => {
    if (!states) {
      if (event.status === EventStatus.approved) {
        return icons.green;
      }
      if (event.status === EventStatus.approved_warning) {
        return icons.orange;
      }
      if (event.status === EventStatus.rejected) {
        return icons.red;
      }
      return icons.gray;
    }

    if (states[event.id] === EventStatus.approved) {
      return icons.green;
    }
    if (states[event.id] === EventStatus.approved_warning) {
      return icons.orange;
    }
    if (states[event.id] === EventStatus.rejected) {
      return icons.red;
    }
    return icons.gray;
  };

  const addEventMarkersToMap = () => {
    // Remove all markers (prevent memory leak and duplicate markers)
    markers?.current?.forEach((marker) => {
      marker.setMap(null);
      marker = null;
    });

    let newMarkers: google.maps.Marker[] = [];
    const getLocationFunc = projectedLocation ? getEventCoords : getOriginalEventCoords;

    if (isAggregated) {
      // Now add new markers
      newMarkers = subEvents.map((subEvent, idx) => {
        const coords = getLocationFunc(subEvent);
        return new google.maps.Marker({
          position: { lat: coords.lat, lng: coords.lon },
          map,
          icon: getIconImage(subEvent),
          title: idx.toString(),
        });
      });
    } else {
      const coords = getLocationFunc(event);
      newMarkers = [
        new google.maps.Marker({
          position: { lat: coords.lat, lng: coords.lon },
          map,
          icon: getIconImage(event),
          title: event.id.toString(),
        }),
      ];
    }

    if (projectedLocation) {
      const egoCoords = getOriginalEventCoords(event);
      const eventHeading = event?.metadata?.heading || event?.metadata?.route_metadata?.heading || 0;
      const mapHeading = map?.getHeading() || 0;
      newMarkers.push(
        new google.maps.Marker({
          position: { lat: egoCoords.lat, lng: egoCoords.lon },
          map,
          icon: createRotatedIcon(eventHeading, mapHeading),
          title: 'Ego location',
        }),
      );
    }

    markers.current = newMarkers;
  };

  const getAvgHeading = async () => {
    if (!isAggregated) {
      const heading = event?.metadata?.heading || event?.metadata?.route_metadata?.heading || 0;
      setHeading(heading);
      return heading;
    }

    const allMetadataPromise = subEvents.map((se) => EventsService.getEventMetadata(se.id));
    const allMetadata = await Promise.all(allMetadataPromise);
    const metadataWithHeadings = allMetadata.filter((m) => m.heading);
    const countOfHeadings = metadataWithHeadings.length;
    const avgHeading = countOfHeadings ? metadataWithHeadings.reduce((acc, curr) => acc + curr.heading, 0) / countOfHeadings : 0;
    setHeading(avgHeading);
    return avgHeading;
  };

  const setupStreetView = async (showAlert = false) => {
    if (!StreetViewService || PanoramaSources.length === 0) {
      return;
    }

    const newHeading = await getAvgHeading();

    const svs = new StreetViewService();
    const newPanorama = getStreetView();

    // Request only official Google outdoor panoramas near the event location.
    // Using both GOOGLE and OUTDOOR as an intersection filter:
    //   - GOOGLE  → excludes all community/user-contributed PhotoSpheres
    //   - OUTDOOR → excludes indoor imagery (PhotoSpheres are also excluded
    //               because their indoor/outdoor status is unknown to the API)
    let streetViewRequest: google.maps.StreetViewResponse;
    try {
      streetViewRequest = await svs.getPanorama({
        location: center,
        radius: 50,
        sources: PanoramaSources,
      });
    } catch {
      if (showAlert && center.lat && center.lng) {
        new Alert('No official Google Street View coverage found for this location.').alert();
      }

      return;
    }

    const foundPanoId = streetViewRequest?.data?.location?.pano;
    if (foundPanoId) {
      // Record as a known-good Google panorama so the navigation guard
      // has a safe target to revert to if needed.
      lastGoodPanoRef.current = foundPanoId;
    }

    if (newPanorama && foundPanoId) {
      // Always navigate to the exact validated pano ID rather than using
      // setPosition (which searches by lat/lng and can land on a community
      // panorama). setPano is precise and works for both the initial mount
      // (shouldMove=false) and explicit reopens (shouldMove=true).
      // Set pendingSetupPanoRef BEFORE setPano — pano_changed can fire on
      // the very next microtask, so the guard must already be suppressed.
      pendingSetupPanoRef.current = foundPanoId;
      newPanorama.setPano(foundPanoId);

      newPanorama.setPov({
        heading: newHeading ?? 0,
        pitch: -10,
      });
    }
  };

  const handleToggleSV = () => {
    if (!panoramaVisible) {
      // manually enabling street view
      setupStreetView(true);
    }
    // https://issuetracker.google.com/issues/35830290?pli=1 thats why we need setzoom
    getStreetView()?.setZoom(1);
    getStreetView()?.setVisible(!panoramaVisible);
    setPanoramaVisibleState(!panoramaVisible);
  };

  const handleKMLClick = () => {
    setPendingOverrideRouteId(null);
    document.getElementById('hiddenFileInput')?.click();
    return false;
  };

  const handleRouteOverride = (routeId: number) => {
    setPendingOverrideRouteId(routeId);
    document.getElementById('hiddenFileInput')?.click();
  };

  const handleMapTypeClick = () => {
    if (mapType === MapTypes.Hybrid) {
      setMapType(MapTypes.Roadmap);
    } else {
      setMapType(MapTypes.Hybrid);
    }
  };

  const fileChanged = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    const results = await parseLocalKMLFile(file);

    if (pendingOverrideRouteId !== null) {
      setLocalOverride(pendingOverrideRouteId, { data: results, filename: file.name });
      setPendingOverrideRouteId(null);
    } else {
      addUserKML(file.name, results);
    }

    // Reset input so the same file can be re-selected
    e.target.value = '';
  };

  const drawOverlays = () => {
    clearAllFromMap();
    const isSVvisible = getStreetView()?.getVisible();
    const effectiveKML = computeEffectiveKML();
    if (!map || !effectiveKML) {
      return;
    }

    if (isSVvisible) {
      enforcementMarkersForSV.current = drawKMLPolygonsAsPointsonMap(map, effectiveKML, center, routeEventTypes);
    } else {
      polylines.current = drawKMLonMap(map, effectiveKML, routeEventTypes);
    }
  };

  const toggleRouteVisibility = () => {
    if (route && route.is_global) {
      toggleHiddenRouteId(route.id);
    }
  };

  const handleToggleUserKML = (id: number) => {
    toggleUserKMLVisibility(id);
  };

  const handleRemoveUserKML = (id: number) => {
    removeUserKML(id);
  };

  useEffect(() => {
    if (map && event) {
      setupStreetView();
      addEventMarkersToMap();
      const streetView = map.getStreetView();

      // Add listener for when Street View is toggled externally (via pegman or google ui)
      const streetViewChangedListener = streetView?.addListener('visible_changed', () => {
        const isVisible = !!map.getStreetView().getVisible();
        setPanoramaVisibleState(isVisible);
        drawOverlays();
      });

      // Navigation guard: fires every time the user moves to a new panorama
      // (arrow clicks, pegman drops, etc.). Re-validates the new panorama and
      // reverts to the last confirmed Google panorama if community content is
      // detected. The copyright check is the most reliable signal available —
      // official Google panoramas always carry "© … Google" in their copyright
      // string, while user-contributed panoramas carry the contributor's name.
      // The links check is a secondary signal: Google car/trekker panoramas
      // form connected sequences with multiple directional links, whereas
      // standalone PhotoSpheres typically have zero navigation links.
      const panoChangedListener = streetView?.addListener('pano_changed', () => {
        const currentPano = streetView.getPano();
        if (!currentPano) return;

        // If a programmatic setup is in flight, skip guard logic entirely.
        // We clear the pending marker when the expected pano ID arrives. If
        // Google instead resolves that navigation to a neighbouring pano ID,
        // pendingSetupPanoRef will remain set and this guard will keep
        // bypassing validation until a later setup run updates or clears it.
        if (pendingSetupPanoRef.current !== null) {
          if (currentPano === pendingSetupPanoRef.current) {
            pendingSetupPanoRef.current = null;
          }
          return;
        }

        if (currentPano === lastGoodPanoRef.current) return;

        const svs = new StreetViewService();
        const requestedPano = currentPano;
        svs
          .getPanorama({ pano: requestedPano })
          .then(({ data }) => {
            // Ignore stale responses — the user may have navigated again
            // before this async request resolved.
            if (streetView.getPano() !== requestedPano) return;

            const copyright = data?.copyright ?? '';
            const hasLinks = (data?.links?.length ?? 0) > 0;
            const isOfficialGoogle = copyright.toLowerCase().includes('google') && hasLinks;

            if (isOfficialGoogle) {
              lastGoodPanoRef.current = requestedPano;
            } else if (lastGoodPanoRef.current) {
              // Revert silently — the user won't see a flash because setPano is
              // synchronous and the panorama hasn't finished rendering yet.
              streetView.setPano(lastGoodPanoRef.current);
            }
          })
          .catch(() => {
            if (lastGoodPanoRef.current) {
              streetView.setPano(lastGoodPanoRef.current);
            }
          });
      });

      // Add listener for map heading changes to update marker rotation
      const headingChangedListener = map.addListener('heading_changed', () => {
        if (projectedLocation) {
          addEventMarkersToMap();
        }
      });

      return () => {
        google.maps.event.removeListener(streetViewChangedListener);
        google.maps.event.removeListener(panoChangedListener);
        google.maps.event.removeListener(headingChangedListener);
      };
    }
    return nothing;
    // eslint-disable-next-line max-len
  }, [map, remoteKML, localOverridesData, event?.id, setPanoramaVisibleState, hash(routeEventTypes), projectedLocation]);

  useEffect(() => {
    const streetView = getStreetView();
    streetView?.setPov({
      heading,
      pitch: -10,
    });
  }, [map, heading]);

  useEffect(() => {
    setupStreetView();
  }, [event?.id]);

  useEffect(() => {
    drawOverlays();
  // eslint-disable-next-line max-len
  }, [remoteKML, localOverridesData, hiddenRouteIdsData, userKMLs, eventRouteHidden, map, panorama, event?.id, hash(routeEventTypes)]);

  useEffect(() => {
    if (event) {
      addEventMarkersToMap();
    }
  }, [states]);

  const loadRemoteRoutefile = async () => {
    if (!route) {
      return;
    }
    try {
      const routeDefinition = await queryClient.ensureQueryData({
        queryKey: ['routes', 'definitions', route.id, fleet.id],
        queryFn: () => RouteService.loadRawRouteDefinition(route.id),
      });

      const parsedKML = extractGoogleCoords(routeDefinition);
      setRemoteKML(parsedKML);
    } catch (e) {
      setRemoteKML(null);
    }
  };

  const loadGlobalRoutefile = async () => {
    if (!route) {
      return;
    }
    try {
      const definition = await queryClient.ensureQueryData({
        queryKey: ['routes', 'definitions', String(route.id), fleet.id],
        queryFn: () => RouteService.loadRawRouteDefinition(route.id),
      });
      setRemoteKML(extractGoogleCoords(definition));
    } catch {
      setRemoteKML(null);
    }
  };

  const downloadGlobalRoute = () => {
    if (!route) {
      return;
    }
    const cached = queryClient.getQueryData<string>(['routes', 'definitions', String(route.id), fleet.id]);
    if (cached) {
      downloadTextFile(cached, `${route.name}.kml`);

      return;
    }
    RouteService.loadRawRouteDefinition(route.id)
      .then((res) => {
        if (res) {
          downloadTextFile(res, `${route.name}.kml`);
        }
      })
      .catch(() => {
        new Alert('Failed to download route definition', 'OK', true).alert();
      });
  };

  const downloadRouteDefinition = (evt: React.MouseEvent<HTMLElement>) => {
    evt.stopPropagation();
    evt.preventDefault();

    if (event?.route_id) {
      RouteService.loadRawRouteDefinition(event.route_id).then((res) => {
        downloadTextFile(res, (kmlFilename || 'route') + '.kml');
      });
    }

    return false;
  };

  useEffect(() => {
    if (route && route.is_global) {
      loadGlobalRoutefile();
    } else if (event?.route_id) {
      setEventRouteHidden(false);
      loadRemoteRoutefile();
    }
  }, [event?.route_id, fleet?.id, route?.id]);

  if (!event) {
    return null;
  }

  const userKMLControls = (
    <>
      {userKMLs.map((kml) => (
        <Kebab key={kml.id} as={Button} size="sm" variant={kml.visible ? 'primary' : 'secondary'} toggleContent={kml.filename}>
          <Dropdown.Item onClick={() => handleToggleUserKML(kml.id)}>{kml.visible ? 'Hide' : 'Show'}</Dropdown.Item>
          <Dropdown.Item onClick={() => handleRemoveUserKML(kml.id)}>Remove</Dropdown.Item>
        </Kebab>
      ))}
      <Tooltip tooltip="Upload local file">
        <Button size="sm" variant="secondary" onClick={handleKMLClick}>
          ↑
        </Button>
      </Tooltip>
    </>
  );

  return (
    <Relative className={props.className} style={{ display: 'inline-block', ...style }}>
      <GoogleMap
        onMapLoad={setMap}
        center={center}
        zoom={20}
        heading={heading}
        tilt={isInternalUser ? 0 : undefined}
        style={{ flexGrow: '1', aspectRatio: '16/9', width: '100%' }}
        mapTypeId={mapType}
        mapTypeControl={false}
        streetView={panorama}
        mapId="streetview"
        controlSize={24}
        // Filter the Pegman drag-drop control to only show official Google
        // coverage (GOOGLE), matching the sources used in
        // getPanorama() so the blue coverage lines are consistent with what
        // the user can actually navigate to. Only passed once the library has
        // loaded so the sources array is never undefined.
        streetViewControlOptions={{ sources: [GoogleStreetViewSource ?? 'google'] }}
      />

      <InternalOnly>
        <ButtonContainer topLeft>
          <Button size="sm" variant={'secondary'} onClick={toggleSettings}>
            ⛭
          </Button>
        </ButtonContainer>
      </InternalOnly>

      <ButtonContainer>
        {route && route.is_global ? (
          <>
            {(() => {
              const override = localOverridesData?.[route.id];
              const label = override ? override.filename : route.name;
              const isHidden = hiddenRouteIdsData?.has(route.id) ?? false;

              return (
                <Kebab as={Button} size="sm" variant={isHidden ? 'secondary' : 'primary'} toggleContent={label}>
                  <Dropdown.Item onClick={toggleRouteVisibility}>{isHidden ? 'Show' : 'Hide'}</Dropdown.Item>
                  <InternalOnly>
                    <Dropdown.Item onClick={downloadGlobalRoute}>Download</Dropdown.Item>
                  </InternalOnly>
                  <Dropdown.Item onClick={() => handleRouteOverride(route.id)}>Upload local file</Dropdown.Item>
                </Kebab>
              );
            })()}
            {userKMLControls}
          </>
        ) : (
          <>
            {(() => {
              const eventOverride = route?.id ? localOverridesData?.[route.id] : null;
              const eventLabel = eventOverride?.filename ?? kmlFilename ?? 'No route';

              return (
                <Kebab as={Button} size="sm" variant={remoteKML && !eventRouteHidden ? 'primary' : 'secondary'} toggleContent={eventLabel}>
                  <Dropdown.Item onClick={() => setEventRouteHidden((h) => !h)}>{eventRouteHidden ? 'Show' : 'Hide'}</Dropdown.Item>
                  <InternalOnly>
                    <Dropdown.Item onClick={downloadRouteDefinition}>Download</Dropdown.Item>
                  </InternalOnly>
                  {route?.id && <Dropdown.Item onClick={() => handleRouteOverride(route.id)}>Upload local file</Dropdown.Item>}
                </Kebab>
              );
            })()}
            {userKMLControls}
          </>
        )}

        <Button size="sm" variant={panoramaVisibleState ? 'primary' : 'secondary'} onClick={handleToggleSV} data-accesskey="KeyM">
          SV
        </Button>

        {!panoramaVisible && (
          <Button size="sm" variant={mapType === 'hybrid' ? 'primary' : 'secondary'} onClick={handleMapTypeClick}>
            Satellite
          </Button>
        )}

        <HiddenFileInput type="file" id="hiddenFileInput" onChange={fileChanged} />
      </ButtonContainer>

      {settingsModal && <SettingsModal toggleSettings={toggleSettings} />}
    </Relative>
  );
};
